import { ConfigProvider, message } from 'antd';
import type { UploadFile } from 'antd/es/upload';
import { useEffect, useState } from 'react';

import { uploadQuestionnaireFile } from '@/apis';
import { useApiHandler } from '@/hooks';

import * as Style from './Upload.style';

interface Props {
  value?: string; // Form.Item 傳入的值（已上傳的 objectPath）
  onChange?: (objectPath: string) => void; // Form.Item 的 onChange
  fileUrl?: string; // 從後端獲取的檔案 URL
  questionnaireId?: string; // 問卷 ID
  questionId?: string; // 問題 ID
}

const Upload = ({
  value,
  onChange,
  questionnaireId,
  questionId,
}: Props) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { handleApiCall } = useApiHandler();

  // 同步 Form.Item 的 value 到 fileList（用於顯示已上傳的檔案）
  useEffect(() => {
    if (value && value.startsWith('questionnaire-files/')) {
      const pathParts = value.split('/');
      const fileName = pathParts[pathParts.length - 1];
      setFileList([
        {
          uid: value,
          name: fileName,
          status: 'done',
        },
      ]);
    } else if (!value) {
      setFileList([]);
    }
  }, [value]);

  // 清理 blob URLs
  useEffect(() => {
    return () => {
      fileList.forEach((file) => {
        if (file.url && file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
      });
    };
  }, [fileList]);

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上傳圖片檔案!');
      return false;
    }

    // 檢查圖片尺寸
    return new Promise<boolean>((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const img = new Image();
        img.onload = async () => {
          const width = img.width;
          const height = img.height;

          // 驗證比例必須為 4:3 (允許 1% 的誤差)
          const aspectRatio = width / height;
          const targetRatio = 4 / 3;
          const tolerance = 0.01;

          if (Math.abs(aspectRatio - targetRatio) > tolerance) {
            message.error('圖片比例必須為 4:3!');
            resolve(false);
            return;
          }

          // 如果沒有 questionnaireId 或 questionId，無法上傳
          if (!questionnaireId || !questionId) {
            message.error('缺少問卷 ID 或問題 ID，無法上傳');
            resolve(false);
            return;
          }

          try {
            // 顯示上傳中的狀態
            const uploadingUid = `uploading_${Date.now()}`;
            setFileList([
              {
                uid: uploadingUid,
                name: file.name,
                status: 'uploading',
              },
            ]);

            const uploadResult = await handleApiCall(
              () => uploadQuestionnaireFile(questionnaireId, questionId, file),
              { showLoading: true, context: '上傳檔案中' },
            );

            if (uploadResult) {
              // 從 objectPath 提取檔案名稱
              const pathParts = uploadResult.objectPath.split('/');
              const fileName = pathParts[pathParts.length - 1];

              // 上傳成功，更新檔案列表狀態
              setFileList([
                {
                  uid: uploadResult.objectPath,
                  name: fileName,
                  status: 'done',
                },
              ]);

              // 通知父組件上傳成功，傳遞 objectPath
              onChange?.(uploadResult.objectPath);
            } else {
              message.error('上傳失敗');
              setFileList([]);
            }
          } catch (error) {
            console.error('Upload failed:', error);
            message.error('上傳失敗');
            setFileList([]);
          }

          resolve(false); // 阻止 antd Upload 的自動上傳
        };
        img.onerror = () => {
          message.error('無法讀取圖片!');
          resolve(false);
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = () => {
        message.error('無法讀取檔案!');
        resolve(false);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemove = () => {
    // 清理 blob URL
    fileList.forEach((file) => {
      if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
      }
    });

    setFileList([]);
    onChange?.('');
  };

  return (
    <ConfigProvider
      theme={{
        cssVar: true,
        hashed: false,
        token: {
          borderRadius: 0,
          colorBorder: '#0e2a27',
          colorBgBase: '#ffffff00',
          controlHeight: 80,
        },
        components: {
          Button: {
            defaultBg: 'transparent',
            defaultBorderColor: '#0d2926',
            defaultColor: '#0d2926',
            defaultHoverColor: '#0d2926',
            defaultHoverBg: '#9db98b',
            defaultHoverBorderColor: '#9db98b',
            defaultActiveBg: '#9db98b',
            defaultActiveBorderColor: '#9db98b',
            defaultActiveColor: '#0d2926',
          },
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
        }}
      >
        <Style.Upload
          fileList={fileList}
          beforeUpload={beforeUpload}
          onRemove={handleRemove}
          showUploadList={{
            showPreviewIcon: false,
            showRemoveIcon: false,
          }}
          accept="image/*"
        >
          <Style.Button variant="outlined" shape="round">
            選擇照片
          </Style.Button>
        </Style.Upload>
      </div>
    </ConfigProvider>
  );
};

export default Upload;
